/**
 * Front-coded word pack codec.
 *
 * Sorted words share long prefixes, so each entry stores only how many
 * characters to keep from the previous word plus its own tail. ENABLE drops
 * from 1.7 MB to about 700 KB before transport compression and to roughly
 * 240 KB after it — the difference between a snappy first load on a phone and
 * a visibly stalled one.
 *
 * It lives in the engine because all three consumers need to agree on it
 * byte for byte: the build script writes packs, the client reads them, and
 * the server reads them to validate independently of the client.
 */
export const MAGIC = 'NT2DICT1';

export interface PackMeta {
  id: string; name: string; licence: string; attribution: string;
  count: number; minLength: number; maxLength: number;
}

const dec = (ch: string): number => {
  const c = ch.charCodeAt(0);
  return c <= 57 ? c - 48 : c - 87;
};

export function decodePack(text: string): { meta: PackMeta; words: Set<string> } {
  const nl1 = text.indexOf('\n');
  if (text.slice(0, nl1) !== MAGIC) throw new Error('Not a word pack.');
  const found = text.indexOf('\n', nl1 + 1);
  const nl2 = found === -1 ? text.length : found;
  const meta = JSON.parse(text.slice(nl1 + 1, nl2)) as PackMeta;

  const words = new Set<string>();
  let prev = '';
  let i = nl2 + 1;
  const n = text.length;
  while (i < n) {
    let end = text.indexOf('\n', i);
    if (end === -1) end = n;
    if (end > i) {
      const word = prev.slice(0, dec(text[i] as string)) + text.slice(i + 1, end);
      words.add(word);
      prev = word;
    }
    i = end + 1;
  }
  return { meta, words };
}

/** Digits 0-9 then a-z; prefix lengths above 35 are clamped (words are short). */
const enc = (n: number): string =>
  n < 10 ? String(n) : String.fromCharCode(87 + Math.min(n, 35));

export function encodePack(meta: PackMeta, sortedWords: readonly string[]): string {
  const lines: string[] = [MAGIC, JSON.stringify(meta)];
  let prev = '';
  for (const word of sortedWords) {
    let shared = 0;
    const max = Math.min(prev.length, word.length, 35);
    while (shared < max && prev.charCodeAt(shared) === word.charCodeAt(shared)) shared++;
    lines.push(enc(shared) + word.slice(shared));
    prev = word;
  }
  return lines.join('\n');
}
